import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const TutorialDialog = ({ text, position = 'bottom', onDismiss, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState(null);
  const dialogRef = useRef(null);
  const parentRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay || 200);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!visible || !parentRef.current) return;

    const update = () => {
      const parent = parentRef.current?.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const dialogW = 260;
      const dh = dialogRef.current?.offsetHeight || 60;
      let left = rect.left + rect.width / 2 - dialogW / 2;
      let top;

      if (position === 'top' || position === 'above') {
        top = rect.top - dh - 12;
      } else {
        top = rect.bottom + 12;
      }

      // Keep within screen
      if (left < 8) left = 8;
      if (left + dialogW > window.innerWidth - 8) left = window.innerWidth - 8 - dialogW;
      // Don't go above screen
      if (top < 8) top = 8;
      // Don't overlap below the parent element
      if (position === 'top' || position === 'above') {
        if (top + dh > rect.top) top = rect.top - dh - 4;
      }

      setCoords({ top, left });
    };

    update();
    requestAnimationFrame(update);

    // Re-position on scroll of any ancestor
    const scrollParents = [];
    let el = parentRef.current?.parentElement;
    while (el) {
      if (el.scrollHeight > el.clientHeight) {
        el.addEventListener('scroll', update, { passive: true });
        scrollParents.push(el);
      }
      el = el.parentElement;
    }
    window.addEventListener('resize', update, { passive: true });

    return () => {
      scrollParents.forEach(sp => sp.removeEventListener('scroll', update));
      window.removeEventListener('resize', update);
    };
  }, [visible, position]);

  if (!visible) return <span ref={parentRef} className="hidden" />;

  const arrowDir = position === 'top' || position === 'above' ? 'down' : 'up';
  const lang = (() => {
    try {
      const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
        || (typeof navigator !== 'undefined' && navigator.language) || '';
      return String(code).toLowerCase().startsWith('ru') ? 'ru' : 'en';
    } catch {
      return 'en';
    }
  })();
  const tapHint = lang === 'ru' ? 'нажми, чтобы продолжить' : 'tap to continue';

  return (
    <>
      <span ref={parentRef} className="hidden" />
      {createPortal(
        <div
          ref={dialogRef}
          className="fixed z-[9999] animate-tut-dialog-in"
          style={{
            top: coords?.top || 0,
            left: coords?.left || 0,
            width: 260,
            pointerEvents: 'auto',
          }}
          onClick={(e) => { e.stopPropagation(); onDismiss && onDismiss(); }}
          data-testid="tutorial-dialog"
        >
          {/* Gradient hairline frame + glass body + a slow sheen sweep. */}
          <div
            className="relative rounded-2xl p-[1px] overflow-hidden tut-sheen"
            style={{
              background: 'linear-gradient(140deg, rgba(245,208,122,0.85), rgba(212,160,82,0.25) 45%, rgba(245,208,122,0.6))',
              boxShadow: '0 10px 30px rgba(0,0,0,0.55), 0 0 22px rgba(212,160,82,0.22)',
            }}
          >
            <div
              className="relative rounded-2xl px-4 py-3"
              style={{
                background: 'linear-gradient(165deg, rgba(22,22,34,0.97), rgba(12,12,20,0.97))',
                backdropFilter: 'blur(14px)',
              }}
            >
              <p className="text-[13px] text-[#F3EEE2] leading-relaxed">{text}</p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#F5D07A] animate-pulse" />
                <span className="text-[10px] tracking-wide text-[#F5D07A]/55">
                  {tapHint}
                </span>
              </div>
            </div>
          </div>
          {arrowDir === 'down' && (
            <div
              className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] w-0 h-0"
              style={{
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderTop: '8px solid rgba(245,208,122,0.8)',
              }}
            />
          )}
          {arrowDir === 'up' && (
            <div
              className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-[1px] w-0 h-0"
              style={{
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderBottom: '8px solid rgba(245,208,122,0.8)',
              }}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default TutorialDialog;
